import { extractRootDomain } from "@ets/shared";
import { getCookieDomainOverride } from "./injection-config.js";

export interface CookieOptions {
  maxAge: number;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
}

const regexCache = new Map<string, RegExp>();

function getCookieRegex(name: string): RegExp {
  let re = regexCache.get(name);
  if (!re) {
    re = new RegExp(`(?:^|;\\s*)${escapeRegex(name)}=([^;]*)`);
    regexCache.set(name, re);
  }
  return re;
}

export function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(getCookieRegex(name));
  return match ? decodeURIComponent(match[1] as string) : undefined;
}

export function setCookie(name: string, value: string, options: CookieOptions): void {
  if (typeof document === "undefined") return;

  // Precedência: domain explícito nas options > override do injection_config > root do host.
  const domain = options.domain ?? getCookieDomainOverride() ?? getRootDomain();
  let cookie = `${name}=${encodeURIComponent(value)}`;
  cookie += `; Max-Age=${options.maxAge}`;
  cookie += `; Path=${options.path ?? "/"}`;
  if (domain) cookie += `; Domain=${domain}`;
  if (options.sameSite) cookie += `; SameSite=${options.sameSite}`;
  if (options.secure ?? true) cookie += "; Secure";
  document.cookie = cookie;
}

export function deleteCookie(name: string, domain?: string): void {
  setCookie(name, "", { maxAge: 0, domain });
}

function getRootDomain(): string | undefined {
  if (typeof location === "undefined") return undefined;
  return extractRootDomain(location.hostname);
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
